import { NODES, type NodeType } from "@repo/fika-blog/src/nodes"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import { NodeSelection } from "@repo/fika-blog/src/tiptap/pm/state"
import type { IconName } from "@repo/fika-design-system/icon"
import { createMemo, Show } from "solid-js"
import { useI18nContext } from "@/i18n/i18n-solid"
import { Mini } from "../components/mini/mini"
import { Tooltip } from "../components/tooltip"

interface TransformTarget {
  iconName: IconName
  label: string
  value: string
}

interface TransformButtonProps {
  editor: Editor
  selection: () => NodeSelection
}

function TransformButton(props: TransformButtonProps) {
  const { LL } = useI18nContext()
  const nodeInfo = createMemo(() => {
    const sel = props.selection()
    const { $from } = sel

    if (sel instanceof NodeSelection) {
      const node = sel.node
      return {
        node,
        pos: sel.from,
        name: node.type.name as NodeType,
      }
    }

    let depth = $from.depth
    if ($from.parent.type.name === "text") {
      depth = Math.max(1, depth - 1)
    }

    const node = $from.node(depth)
    if (!node) throw Error("No node found")

    const pos = depth === 0 ? 0 : $from.before(depth)
    return {
      node,
      pos,
      name: node.type.name as NodeType,
    }
  })
  const selectedValue = createMemo(() => {
    const info = nodeInfo()
    if (info.name === "heading") {
      const level = info.node.attrs.level
      return `heading-${level}`
    }
    return info.name
  })

  const getTextWrapperTransformTargets = createMemo<TransformTarget[]>(() => [
    {
      value: "paragraph",
      label: LL().editor.text(),
      iconName: "type" satisfies IconName,
    },
    {
      value: "heading-1",
      label: LL().editor.heading1(),
      iconName: "heading-1",
    },
    {
      value: "heading-2",
      label: LL().editor.heading2(),
      iconName: "heading-2",
    },
    {
      value: "heading-3",
      label: LL().editor.heading3(),
      iconName: "heading-3",
    },
    {
      value: "blockquote",
      label: LL().editor.blockquote(),
      iconName: "message-square-quote",
    },
    {
      value: "callout",
      label: LL().editor.callout(),
      iconName: "messages-square",
    },
    {
      value: "codeBlock",
      label: LL().editor.code(),
      iconName: NODES.codeBlock.iconName,
    },
  ])

  const showTransformButton = createMemo(() =>
    getTextWrapperTransformTargets()
      .map(target => target.value)
      .includes(selectedValue()),
  )

  return (
    <Show when={showTransformButton()}>
      <Tooltip description={LL().editor.turnIntoLabel()}>
        <Mini.Select
          items={getTextWrapperTransformTargets()}
          value={selectedValue()}
          hideValue
          portal={false}
          hideChevron
          onChange={value => {
            if (!value) return
            const chain = props.editor.chain().focus()
            const currentNodeType = nodeInfo().name

            if (currentNodeType === "blockquote" && value !== "blockquote") {
              chain.unsetBlockquote()
            }
            if (currentNodeType === "callout" && value !== "callout") {
              chain.unsetCallout()
            }

            switch (value) {
              case "heading-1":
                return chain.setNode("heading", { level: 1 }).run()
              case "heading-2":
                return chain.setNode("heading", { level: 2 }).run()
              case "heading-3":
                return chain.setNode("heading", { level: 3 }).run()
              case "blockquote":
                return chain.setBlockquote().run()
              case "callout":
                return chain.setCallout().run()
              case "paragraph":
                return chain.setParagraph().run()
              case "codeBlock":
                return chain.setNode("codeBlock").run()
              default:
                return false
            }
          }}
        />
      </Tooltip>
      <Mini.Divider />
    </Show>
  )
}

export { TransformButton }
